import { Button, Card, Modal } from "antd";
import Meta from "antd/es/card/Meta";
import { useTranslation } from "react-i18next";

import PropTypes from "prop-types";
import { useState } from "react";

function AddonsCard({ addon, handleDeleteAddon, handleModalState }) {
    const { t } = useTranslation(["addons"]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const showDeleteConfirm = () => {
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        try {
            await handleDeleteAddon(addon, "regular");
        } finally {
            setDeleteLoading(false);
            setIsDeleteModalOpen(false);
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
    };

    const openEdit = () => handleModalState({ isOpen: true, addonToEdit: addon });

    const statusBadge =
        addon.status === "active" ? (
            <div className="flex justify-center items-center rounded-3xl bg-green-400 px-2 py-0.5 text-xs font-medium">
                {t("createAddons.switchOn")}
            </div>
        ) : (
            <div className="flex justify-center items-center rounded-3xl bg-gray-300 px-2 py-0.5 text-xs font-medium">
                {t("createAddons.switchOff")}
            </div>
        );

    return (
        <div className="relative group min-w-0">
            {addon.isRequired ? (
                <div className="pointer-events-none absolute left-3 top-3 z-10">
                    <div className="flex items-center justify-center rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white shadow-sm">
                        {t("createAddons.requiredLabel")}
                    </div>
                </div>
            ) : null}
            <div className="pointer-events-none absolute right-3 top-3 z-10">{statusBadge}</div>
            <Card
                className="h-full overflow-hidden [&_.ant-card-actions]:!hidden lg:[&_.ant-card-actions]:!flex [&_.ant-card-body]:!p-3 sm:[&_.ant-card-body]:!p-4"
                cover={
                    <img
                        alt={t("addonCard.bannerAlt")}
                        src={addon?.image}
                        className="h-36 w-full object-cover sm:h-32"
                    />
                }
                actions={[
                    <div key="price">
                        <span className="font-semibold text-black">${addon?.rate}</span>
                        {t("addonCard.perDay")}
                    </div>,
                ]}>
                <div className="min-h-[4.5rem] pt-6 sm:min-h-[70px] sm:pt-0">
                    <Meta
                        className="text-center [&_.ant-card-meta-title]:!text-base [&_.ant-card-meta-title]:!whitespace-normal"
                        title={addon.name}
                        description={
                            <p className="line-clamp-2 overflow-hidden text-ellipsis text-sm">{addon.desc}</p>
                        }
                    />
                </div>

                <div className="mt-3 space-y-2 border-t border-gray-100 pt-3 lg:hidden">
                        <p className="text-center text-sm font-semibold text-[#2F2F46]">
                            ${addon?.rate}
                            {t("addonCard.perDay")}
                        </p>
                        <div className="flex flex-col gap-2">
                            <Button shape="round" className="!h-10 w-full" onClick={openEdit}>
                                {t("editAddon")}
                            </Button>
                            <Button shape="round" color="danger" variant="outlined" className="!h-10 w-full" onClick={showDeleteConfirm}>
                                {t("deleteAddon")}
                            </Button>
                        </div>
                    </div>
            </Card>

            <div className="absolute inset-0 hidden flex-col items-center justify-center gap-2 rounded-md bg-black bg-opacity-30 opacity-0 transition-opacity duration-300 group-hover:opacity-100 lg:flex">
                    <Button variant="outlined" shape="round" onClick={openEdit}>
                        {t("editAddon")}
                    </Button>
                    <Button variant="outlined" shape="round" color="danger" onClick={showDeleteConfirm}>
                        {t("deleteAddon")}
                    </Button>
                </div>

            <Modal
                title={t("addonCard.confirmDelete")}
                open={isDeleteModalOpen}
                onOk={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                centered
                classNames={{
                    content:
                        "!w-[calc(100vw-2rem)] !max-w-[calc(100vw-2rem)] sm:!w-[480px] sm:!max-w-[480px]",
                }}
                okText={t("_addonDetails.delete")}
                cancelText={t("cancel")}
                okButtonProps={{
                    danger: true,
                    loading: deleteLoading,
                    shape: "round",
                }}
                cancelButtonProps={{ shape: "round" }}
                confirmLoading={deleteLoading}>
                <p>{t("addonCard.deleteConfirmation", { name: addon.name })}</p>
                <p>{t("addonCard.deleteWarning")}</p>
            </Modal>
        </div>
    );
}

AddonsCard.propTypes = {
    addon: PropTypes.object.isRequired,
    handleDeleteAddon: PropTypes.func.isRequired,
    handleModalState: PropTypes.func.isRequired,
};

export default AddonsCard;
